import {
  Direction,
  PrecisionType,
  TradeData,
  TradeDataWithKey,
  TradeSourceType,
} from "@shared/types";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import ExpandingCard from "common/Boxes/ExpandingCard";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography";
import { ICON_TYPES, TRADE_EXTRA_DETAILS_TABS } from "@shared/common/constants";
import React, { useCallback, useContext, useMemo } from "react";
import Col from "antd/es/col";
import Row from "antd/es/row";
import GridProgressGraph from "features/trades/common/GridProgressGraph";
import TradeProgressGraph from "features/trades/common/TradeProgressGraph";
import { NewLookCardProgress } from "features/trades/NewLook/NewLookProgressCell.tsx";
import AutoSizeText from "common/Text/AutoSizeText";
import styled, { useTheme } from "styled-components";
import TradeActionsDropdown from "features/trades/OpenTrades/TradeActionsDropdown";
import {
  NEW_LOOK_TRADE_CARD_COLUMN_SPANS,
  NEW_LOOK_TRADE_ROW_COLUMNS,
  TRADE_CARD_COLUMN_SPANS,
} from "features/trades/common/constants";
import { useTradeCard } from "features/trades/common/commonHooks";
import TradeCardExtraInfo from "features/trades/TradesCards/TradeCardExtraInfo";
import { AccountObjectType } from "@shared/features/settings/types";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import { useInvalidAccount } from "@shared/features/settings/hooks";
import useMedia from "utils/hooks/useMedia.ts";
import { MOBILE_LAYOUT_ONE_COLUMN } from "common/Layouts/TwoChartsTableLayout.tsx";
import { getTradeExtraDetailsRoute } from "utils/routes.tsx";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import IconWithSource from "features/trades/common/IconWithSource.tsx";
import PnlSocketDataProvider from "features/trades/TradesCards/PnlSocketDataProvider.tsx";
import LiveSymbolPrice from "common/LiveComponents/LiveSymbolPrice.tsx";
import Checkbox from "common/Checkbox/Checkbox.tsx";
import {
  CardsSelectorContext,
  ContextProps,
} from "@shared/common/CardsList/CardsSelector.context.ts";
import { TRADE_CARD_QUANTITY_ID, TRADE_CARD_ROW_ID } from "testIds";
import { useDisplayTradeDetails } from "@shared/utils/hooks/misc.ts";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import {
  getNewLookListRowStyle,
  NewLookListRow,
} from "features/trades/NewLook/newLookListRows.ts";

interface TradeCardProps {
  tradeData: TradeDataWithKey;
  isOpenTrades?: boolean;
  toggleExpanded: (key: string | number) => void;
  isExpanded: boolean;
  allowTradeClick?: boolean;
  shouldFetchSocketPnl?: boolean;
  showBulkActions?: boolean;
  /**
   * New look: the card is a row of a list panel (hairline separators), the
   * last one closing the panel.
   */
  listRow?: NewLookListRow;
}

const TradeCard = ({
  tradeData,
  isOpenTrades,
  toggleExpanded,
  isExpanded,
  allowTradeClick,
  shouldFetchSocketPnl = true,
  showBulkActions,
  listRow,
}: TradeCardProps) => {
  const { isDrawer } = useContext(DrawerContext) || {};
  const theme = useTheme();
  const { historyPush } = useHistoryWithQuery();
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_LAYOUT_ONE_COLUMN})`],
    [true],
    false
  );
  const bulkActionsRight = useMedia([`(max-width: 390px)`], [-4], 0);
  const { getPrecision } = usePrecisions();
  const pricePrecision = getPrecision(
    PrecisionType.PRICE,
    tradeData.symbol,
    tradeData.exchange
  );
  const { getInvalidAccountData } = useInvalidAccount();
  const invalidAccountData = useMemo(
    () => getInvalidAccountData(tradeData.client_id),
    [getInvalidAccountData, tradeData.client_id]
  );
  const spans = theme.isNewLook
    ? NEW_LOOK_TRADE_CARD_COLUMN_SPANS
    : TRADE_CARD_COLUMN_SPANS;
  const { getTradeAmount, getTradeQuantity } = useTradeCard({
    allowTradeClick,
  });

  const { getCheckboxProps, isEditMode } =
    useContext<ContextProps<TradeData> | undefined>(CardsSelectorContext) || {};
  const checkboxProps = getCheckboxProps?.(tradeData);
  const displayTradeDetails = useDisplayTradeDetails();

  const onLongPress = useCallback(() => {
    const isGridBot = tradeData.source_type === TradeSourceType.GRID_BOT;
    historyPush(
      getTradeExtraDetailsRoute(
        location.pathname,
        tradeData.id,
        TRADE_EXTRA_DETAILS_TABS[isGridBot ? "GRIDS" : "ORDERS"]
      )
    );
  }, [tradeData]);

  const liveSymbolPriceTextProps = useMemo(
    () =>
      ({
        iconType: ICON_TYPES.COIN,
        coinOrExchangeName: tradeData.symbol?.split("/")[1],
        iconSize: "s",
      } as const),
    [tradeData]
  );

  const symbol = useMemo(
    () =>
      theme.isNewLook ? (
        <Col span={spans[0]}>
          <NewLookSymbol tradeData={tradeData} />
        </Col>
      ) : (
        <Col span={spans[0]}>
          <SymbolWrapper>
            <IconWithSource
              symbol={tradeData.symbol}
              sourceType={tradeData.source_type}
              source={tradeData.group}
              mainIconSize={26}
            />

            <SymbolContent>
              <InactiveAccountIcon
                objectType={AccountObjectType.TRADE}
                accountId={tradeData.client_id}
                style={{ width: "100%" }}
                hideIcon={true}
                placement="topLeft"
              >
                <AutoSizeText
                  style={{
                    color:
                      invalidAccountData?.color ?? theme.colors.primaryGrey,
                  }}
                >
                  {tradeData.symbol}
                </AutoSizeText>
              </InactiveAccountIcon>
              {isOpenTrades && (
                <LiveSymbolPrice
                  symbol={tradeData.symbol}
                  exchange={tradeData.exchange}
                  initialPrice={tradeData.price}
                  fontSize={typography.fontSizes.S}
                  textProps={liveSymbolPriceTextProps}
                  textColor={theme.colors.secondaryGrey}
                />
              )}
            </SymbolContent>
          </SymbolWrapper>
        </Col>
      ),
    [
      tradeData,
      isOpenTrades,
      invalidAccountData.color,
      theme,
      pricePrecision,
      spans,
    ]
  );

  const amount = useMemo(
    () =>
      theme.isNewLook ? (
        <Col span={spans[1]}>
          <NewLookAmount>
            <span>{getTradeAmount(tradeData)}</span>
            <span data-testid={TRADE_CARD_QUANTITY_ID}>
              {getTradeQuantity(tradeData)}
            </span>
          </NewLookAmount>
        </Col>
      ) : (
        <Col span={spans[1]}>
          <Flex $flexDirection="column" $alignItems="flex-start">
            <Text as="div" $weight={typography.fontWeights.MEDIUM}>
              {getTradeAmount(tradeData)}
            </Text>
            <Text
              as="div"
              type={"secondary"}
              data-testid={TRADE_CARD_QUANTITY_ID}
            >
              {getTradeQuantity(tradeData)}
            </Text>
          </Flex>
        </Col>
      ),
    [getTradeAmount, getTradeQuantity, tradeData, spans, theme.isNewLook]
  );

  const chart = useMemo(
    () => (
      <Col span={spans[2]}>
        {theme.isNewLook ? (
          <NewLookCardProgress
            tradeData={tradeData}
            isOpenTrades={isOpenTrades}
          />
        ) : "current_price" in tradeData.graph_data ? (
          <GridProgressGraph tradeData={tradeData} />
        ) : (
          <TradeProgressGraph
            tradeData={tradeData}
            size={"s"}
            style={GRAPH_STYLE}
          />
        )}
      </Col>
    ),
    [tradeData, theme.isNewLook, spans, isOpenTrades]
  );

  const collapsibleContent = useMemo(() => {
    return (
      <TradeCardExtraInfo tradeData={tradeData} isOpenTrades={isOpenTrades} />
    );
  }, [tradeData, isOpenTrades]);

  const actions = useMemo(() => {
    if (isEditMode && checkboxProps) {
      return (
        <ActionsWrapper
          onClick={(e: React.MouseEvent) => {
            checkboxProps?.onClick();
            e.stopPropagation();
          }}
          style={{ right: bulkActionsRight }}
        >
          <Checkbox checked={checkboxProps?.isSelected} $size={18} />
        </ActionsWrapper>
      );
    }
    return (
      !isDrawer && (
        <ActionsWrapper onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <TradeActionsDropdown
            tradeData={tradeData}
            isCardsView={true}
            isOpenTrades={isOpenTrades}
            showBulkActions={showBulkActions}
          />
        </ActionsWrapper>
      )
    );
  }, [
    isDrawer,
    tradeData,
    isOpenTrades,
    isEditMode,
    checkboxProps?.isSelected,
    bulkActionsRight,
    showBulkActions,
  ]);

  const cardStyle = useMemo(() => {
    if (theme.isNewLook && listRow) {
      // A row of the list panel: its side edges and a hairline under it.
      return getNewLookListRowStyle(theme, listRow);
    }
    return {
      padding: theme.isNewLook ? "10px 14px" : "8px 12px",
      cursor: "pointer",
      background: isMobile
        ? theme.colors.cardBgColor
        : theme.colors.secondaryCardBgColor,
    };
  }, [theme, isMobile, listRow]);

  const setExpanded = useCallback(() => {
    toggleExpanded(tradeData.key);
  }, [tradeData]);

  return (
    <ExpandingCard
      setExpanded={setExpanded}
      expanded={isExpanded}
      collapsibleContent={collapsibleContent}
      style={cardStyle}
      showArrow={isDrawer}
      onLongPress={displayTradeDetails ? onLongPress : undefined}
    >
      <PnlSocketDataProvider
        tradeId={tradeData.id}
        skip={!shouldFetchSocketPnl}
      >
        <TradeRow style={ROW_STYLE} data-testid={TRADE_CARD_ROW_ID}>
          {symbol}
          {amount}
          {chart}
          {actions}
        </TradeRow>
      </PnlSocketDataProvider>
    </ExpandingCard>
  );
};

export default React.memo(TradeCard);

const ROW_STYLE = { alignItems: "center" };

/** New look: the dashboard's row lead, symbol over its side and leverage. */
const NewLookSymbol = ({ tradeData }: { tradeData: TradeData }) => {
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const side = tradeData["Long/Short"];
  return (
    <SymbolWrapper>
      <IconWithSource
        symbol={tradeData.symbol}
        sourceType={tradeData.source_type}
        source={tradeData.group}
        mainIconSize={28}
      />
      <NewLookNames>
        <InactiveAccountIcon
          objectType={AccountObjectType.TRADE}
          accountId={tradeData.client_id}
          style={{ width: "100%", minWidth: 0 }}
          hideIcon={true}
          placement="topLeft"
        >
          <NewLookSymbolName title={tradeData.symbol}>
            {tradeData.symbol}
          </NewLookSymbolName>
        </InactiveAccountIcon>
        <NewLookSubLine>
          {side && (
            <span
              style={{
                color: side === Direction.SHORT ? tokens.down : tokens.up,
                fontWeight: 500,
              }}
            >
              {side}
            </span>
          )}
          {!!tradeData.leverage && <span>{tradeData.leverage}x</span>}
        </NewLookSubLine>
      </NewLookNames>
    </SymbolWrapper>
  );
};
const GRAPH_STYLE = { margin: 0 };

const SymbolWrapper = styled(Flex)`
  justify-content: flex-start;
  max-width: 100%;
  padding-right: 8px;
`;
const SymbolContent = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
`;
const NewLookNames = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;
const NewLookSymbolName = styled.div`
  font-size: 14px;
  line-height: 18px;
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const NewLookSubLine = styled.div`
  display: flex;
  gap: 4px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const NewLookAmount = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  > span:first-child {
    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
    color: ${(p) => getNewLookTokens(p.theme).ink};
    * {
      font-size: inherit !important;
      font-weight: inherit !important;
      color: inherit !important;
    }
  }
  > span:last-child {
    ${newLookType.meta};
    color: ${(p) => getNewLookTokens(p.theme).muted};
    overflow-wrap: anywhere;
  }
`;
const ActionsWrapper = styled.div`
  right: -9px;
  position: absolute;
`;
const TradeRow = styled(Row)`
  ${(p) =>
    p.theme.isNewLook &&
    `
    display: grid;
    grid-template-columns: ${NEW_LOOK_TRADE_ROW_COLUMNS};
    gap: 8px;
    padding-right: 16px;
    > .ant-col {
      max-width: none;
      min-width: 0;
    }
    > .ant-col:nth-child(2) {
      overflow-wrap: anywhere;
      font-size: 12px;
      line-height: 18px;
      font-variant-numeric: tabular-nums;
    }
  `}
`;
